import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import type { SortingState } from "@tanstack/react-table";
import { cn, formatCurrency } from "@/lib/utils";
import { ChevronsUpDown, Share2 } from "lucide-react";
import { usePanelState } from "@/state/state-storage";
import { OpenOrders, type OpenOrder } from "./open-orders";
import { TradeHistory } from "./trade-history";
import { FundingHistory } from "./funding-history";
import { Positions } from "./positions";
import { EditTpSlModal } from "./edit-tpsl-modal";
import { EditLeverageModal } from "./edit-leverage-modal";
import { EditPositionMarginModal } from "./edit-position-margin-modal";
import { ClosePositionModal } from "./close-position-modal";
import { ShareResultModal } from "./share-result-modal";
import { EditOrderModal } from "./edit-order-modal";
import { useTradeHistory, useFundingHistoryData } from "./use-history";
import { useProductOptions } from "@/features/product/product-options";
import { useProduct, type Product } from "@/features/product/product-context";
import { useActiveLayout } from "@/layout/layout-active-context";
import { Actions, type TabSetNode, type RowNode } from "flexlayout-react";
import { type MarketSymbol } from "@/solana/constants";
import type { Position } from "@/solana/programs/types";
import type { StorePosition } from "@/backend/store";
import { useBackendOrders } from "@/backend/hooks/use-backend-orders";
import { toBackendOrderRequest } from "@/backend/types";
import { OrdersApi, PositionsApi } from "@/backend/api-client";
import { TradingPanelCard } from "@/features/shared/trading-panel-card";
import { usePositions, type ClosePositionData } from "@/solana/hooks/use-positions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { LoginModal } from "@/components/auth/login-modal";
import { useTradingSettings } from "@/features/trading-settings/trading-settings-context";
import { usePriceStore, useAccountStore } from "@/backend/store";
// NOTE: Prices are now in Zustand store - cells subscribe directly via usePrice()
// No more usePriceMap or Context needed at this level

type TabType = "positions" | "open-orders" | "trade-history" | "funding-history";

type PositionsPanelState = {
  activeTab: TabType;
  positionsSorting: SortingState;
  openOrdersSorting: SortingState;
  tradeHistorySorting: SortingState;
  fundingHistorySorting: SortingState;
};

type PositionsPanelProps = {
  panelId: string;
};

export const PositionsPanel = ({ panelId }: PositionsPanelProps) => {
  const [state, setState] = usePanelState<PositionsPanelState>(panelId, {
    activeTab: "positions",
    positionsSorting: [],
    openOrdersSorting: [],
    tradeHistorySorting: [],
    fundingHistorySorting: [],
  });

  // TP/SL Modal state
  const [editTpSlPosition, setEditTpSlPosition] = useState<Position | null>(null);
  const isTpSlModalOpen = editTpSlPosition !== null;

  // Leverage Modal state
  const [editLeveragePosition, setEditLeveragePosition] = useState<StorePosition | null>(null);
  const isLeverageModalOpen = editLeveragePosition !== null;

  // Share Result Modal state
  const [shareModalData, setShareModalData] = useState<{
    position: Position | null;
    pnl: number;
    exitPrice: number;
  } | null>(null);
  const isShareModalOpen = shareModalData !== null;

  // Login Modal state (for showing login prompt when not authenticated)
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Check if user is authenticated
  const { isAuthenticated, userId } = useUnifiedAuth();

  // Positions data (real data from backend)
  // NOTE: Prices are in Zustand store - cells subscribe directly, no priceMap needed here
  const { positions, isLoading: positionsLoading, closePositionAsync, refetch: refetchPositions } = usePositions();

  // Get market prices from price store for order validation
  const marketPrices = usePriceStore((state) => state.marketPrices);

  // Get leverage settings for creating new orders
  const { getLeverage } = useTradingSettings();

  // Handlers for positions
  // Industry-standard pattern: receive data directly from the UI, no store lookup needed
  // If the user can see the position, they can act on it (Binance/Bybit/HyperLiquid pattern)
  const handleEditTpSl = useCallback((position: Position) => {
    // No lookup needed - position data comes directly from the UI
    setEditTpSlPosition(position);
  }, []);

  // State for edit order modal
  const [editOrderModalData, setEditOrderModalData] = useState<{
    order: OpenOrder;
    currentPrice: number;
  } | null>(null);
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);

  const handleClearTpSl = useCallback(async (market: string, type: "tp" | "sl") => {
    if (!userId) return;

    try {
      // Get current position from AccountStore to preserve the other TP/SL value
      const accountPositions = useAccountStore.getState().positions;
      const position = accountPositions.get(market);
      if (!position) return;

      // Use PositionsApi which includes authentication headers
      const response = await PositionsApi.updateTpSl(
        market,
        type === "tp" ? null : (position.takeProfit || null),
        type === "sl" ? null : (position.stopLoss || null)
      );

      // Update the store with the response for immediate UI update
      useAccountStore.getState().applyMutationResult({ position: response.position });

      toast.success(`${type.toUpperCase()} cleared successfully`);
      console.log(`[PositionsPanel] Cleared ${type.toUpperCase()} for ${market}`);
    } catch (error) {
      console.error("[PositionsPanel] Error clearing TP/SL:", error);
      toast.error(`Failed to clear ${type.toUpperCase()}`);
    }
  }, [userId]);

  const handleTpSlModalClose = useCallback((open: boolean) => {
    if (!open) {
      setEditTpSlPosition(null);
    }
  }, []);

  const handleTpSlSuccess = useCallback(() => {
    refetchPositions();
  }, [refetchPositions]);

  // Handlers for leverage editing
  // Accept Position type from table but store as StorePosition (compatible fields)
  const handleEditLeverage = useCallback((position: Position) => {
    // Position and StorePosition share the fields needed by EditLeverageModal
    setEditLeveragePosition(position as unknown as StorePosition);
  }, []);

  const handleLeverageModalClose = useCallback((open: boolean) => {
    if (!open) {
      setEditLeveragePosition(null);
    }
  }, []);

  const handleLeverageSuccess = useCallback(() => {
    // No need to refetch - leverage modal already updated store via updatePositionFromHttp
    // LeverageCell subscribes to store directly, so it will re-render automatically
  }, []);

  // Margin Modal state
  const [editMarginPosition, setEditMarginPosition] = useState<Position | null>(null);
  const isMarginModalOpen = editMarginPosition !== null;

  // Handlers for margin editing
  const handleEditMargin = useCallback((position: Position) => {
    setEditMarginPosition(position);
  }, []);

  const handleMarginModalClose = useCallback((open: boolean) => {
    if (!open) {
      setEditMarginPosition(null);
    }
  }, []);

  const handleMarginSuccess = useCallback(() => {
    refetchPositions();
  }, [refetchPositions]);

  // Close Position Modal state
  const [closeModalPosition, setCloseModalPosition] = useState<Position | null>(null);
  const isCloseModalOpen = closeModalPosition !== null;

  // Get margin mode from TradingSettingsProvider (which reads from Zustand store)
  // This is the single source of truth for margin mode across the app
  const { accountMode: marginMode } = useTradingSettings();

  // Get price store function for fetching latest prices when closing positions
  const getPriceFromStore = usePriceStore((state) => state.getPrice);

  // Open close position modal (instead of immediately closing)
  const handleClosePosition = useCallback(
    (closeData: ClosePositionData) => {
      // Build Position object from closeData to open modal
      const position: Position = {
        id: `${closeData.market}-temp`,
        market: closeData.market as MarketSymbol,
        side: closeData.side,
        size: closeData.size,
        entryPrice: closeData.entryPrice,
        currentPrice: closeData.currentPrice,
        leverage: closeData.leverage,
        liquidationPrice: 0,
        unrealizedPnl: 0,
        margin: closeData.margin,
        collateral: closeData.margin,
        status: "open",
        openedAt: Date.now(),
      };
      
      // Open modal instead of immediate close
      setCloseModalPosition(position);
    },
    []
  );

  // Handle close modal close
  const handleCloseModalClose = useCallback((open: boolean) => {
    if (!open) {
      setCloseModalPosition(null);
    }
  }, []);

  // Execute actual close when user confirms in modal
  const handleConfirmClose = useCallback(
    async (position: Position, closePercentage: number) => {
      // Calculate partial close size
      const closeSize = position.size * (closePercentage / 100);
      
      // GET LATEST PRICE FROM STORE - prevents stale price usage
      // This ensures the price sent to backend matches what user saw in modal
      const latestPrice = getPriceFromStore(position.market);
      const currentPrice = latestPrice > 0 ? latestPrice : position.currentPrice;
      
      const closeData: ClosePositionData = {
        market: position.market,
        side: position.side,
        size: closeSize,  // Partial size based on percentage
        currentPrice: currentPrice,  // Use latest price from store
        entryPrice: position.entryPrice,
        leverage: position.leverage,
        margin: position.margin,
      };
      
      // Store position data before closing for share modal
      const positionForShare: Position = {
        ...position,
        id: `${closeData.market}-${Date.now()}`,
        size: closeSize,  // Use the close size for share modal
      };
      
      try {
        const result = await closePositionAsync(closeData);
        
        if (result.success && result.realizedPnl !== undefined) {
          const isProfit = result.realizedPnl >= 0;
          const pnlFormatted = `${isProfit ? "+" : "-"}$${formatCurrency(Math.abs(result.realizedPnl), 2)}`;
          const percentageText = closePercentage === 100 ? "" : ` (${closePercentage}%)`;
          
          // Show toast with share button - fast animation for snappy feel
          toast.success(`${closeData.market} closed${percentageText}`, {
            description: (
              <div className="flex items-center justify-between gap-4">
                <span>
                  Realized P&L: <span className={isProfit ? "text-bid-foreground" : "text-ask-foreground"}>{pnlFormatted}</span>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs shrink-0"
                  onClick={() => {
                    setShareModalData({
                      position: positionForShare,
                      pnl: result.realizedPnl!,
                      exitPrice: closeData.currentPrice,
                    });
                  }}
                >
                  <Share2 className="size-3 mr-1" />
                  Share
                </Button>
              </div>
            ),
            duration: 8000, // Longer duration to give time to click share
            className: 'fast-toast',
          });
        } else if (!result.success) {
          toast.error(`Failed to close ${closeData.market}`, {
            description: result.error || "Unknown error",
          });
        }
      } catch (error) {
        toast.error(`Failed to close ${closeData.market}`, {
          description: error instanceof Error ? error.message : "Unknown error",
        });
        throw error; // Re-throw so modal can handle it
      }
    },
    [closePositionAsync, getPriceFromStore]
  );

  // Handle share modal close
  const handleShareModalClose = useCallback((open: boolean) => {
    if (!open) {
      setShareModalData(null);
    }
  }, []);

  const handlePositionsSorting = useCallback(
    (sorting: SortingState) => {
      setState((prev) => ({ ...prev, positionsSorting: sorting }));
    },
    [setState]
  );

  // Sort positions based on current sorting state
  const sortedPositions = useMemo(() => {
    if (state.positionsSorting.length === 0) return positions;

    const sorted = [...positions];
    const { id, desc } = state.positionsSorting[0];

    sorted.sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;

      switch (id) {
        case "market":
          aVal = a.market;
          bVal = b.market;
          break;
        case "side":
          aVal = a.side;
          bVal = b.side;
          break;
        case "size":
          aVal = a.size;
          bVal = b.size;
          break;
        case "entryPrice":
          aVal = a.entryPrice;
          bVal = b.entryPrice;
          break;
        case "leverage":
          aVal = a.leverage;
          bVal = b.leverage;
          break;
        case "margin":
          aVal = a.margin;
          bVal = b.margin;
          break;
        case "liquidationPrice":
          aVal = a.liquidationPrice;
          bVal = b.liquidationPrice;
          break;
        default:
          return 0;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        return desc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
      }
      return desc ? (bVal as number) - (aVal as number) : (aVal as number) - (bVal as number);
    });

    return sorted;
  }, [positions, state.positionsSorting]);

  const { model } = useActiveLayout();

  // Vertical resize handler
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startWeightsRef = useRef<{ chartWeight: number; positionsWeight: number } | null>(null);

  const getParentRowWeights = useCallback(() => {
    // Find the tab node
    const tabNode = model.getNodeById(panelId);
    if (!tabNode) return null;

    // Get the tabset (parent of tab) - this is the positions tabset
    const tabSet = tabNode.getParent() as TabSetNode | null;
    if (!tabSet) return null;

    // Get the Left Column Row (parent of positions tabset)
    // In the new two-column layout, positions tabset is a direct child of the left column row
    const leftColumnRow = tabSet.getParent() as RowNode | null;
    if (!leftColumnRow || leftColumnRow.getType() !== "row") return null;

    // Get children of left column row (should be chart+orderbook row and positions tabset)
    const children = leftColumnRow.getChildren();
    if (children.length < 2) return null;

    // First child is the chart+orderbook row, second is positions tabset
    const chartOrderBookRow = children[0] as RowNode;
    const positionsTabSet = children[1] as TabSetNode;

    // Verify we found the correct elements
    if (!chartOrderBookRow || !positionsTabSet) return null;

    return {
      leftColumnRowId: leftColumnRow.getId(),
      chartRowId: chartOrderBookRow.getId(),
      positionsRowId: positionsTabSet.getId(),
      chartWeight: chartOrderBookRow.getWeight(),
      positionsWeight: positionsTabSet.getWeight(),
    };
  }, [model, panelId]);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const weights = getParentRowWeights();
    if (!weights) return;

    isDraggingRef.current = true;
    startYRef.current = e.clientY;
    startWeightsRef.current = {
      chartWeight: weights.chartWeight,
      positionsWeight: weights.positionsWeight,
    };

    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
  }, [getParentRowWeights]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current || !startWeightsRef.current) return;

      const weights = getParentRowWeights();
      if (!weights) return;

      const deltaY = startYRef.current - e.clientY; // Negative = dragging down, Positive = dragging up
      const sensitivity = 0.5; // Adjust sensitivity
      const totalWeight = startWeightsRef.current.chartWeight + startWeightsRef.current.positionsWeight;
      
      // Calculate new weights based on drag
      const weightDelta = (deltaY * sensitivity * totalWeight) / window.innerHeight;
      
      let newChartWeight = startWeightsRef.current.chartWeight - weightDelta;
      let newPositionsWeight = startWeightsRef.current.positionsWeight + weightDelta;

      // Clamp weights to reasonable bounds (min 15%, max 85%)
      const minWeight = totalWeight * 0.15;
      const maxWeight = totalWeight * 0.85;

      if (newChartWeight < minWeight) {
        newChartWeight = minWeight;
        newPositionsWeight = totalWeight - minWeight;
      } else if (newChartWeight > maxWeight) {
        newChartWeight = maxWeight;
        newPositionsWeight = totalWeight - maxWeight;
      }

      // Update FlexLayout model
      model.doAction(Actions.updateNodeAttributes(weights.chartRowId, { weight: newChartWeight }));
      model.doAction(Actions.updateNodeAttributes(weights.positionsRowId, { weight: newPositionsWeight }));
    };

    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        startWeightsRef.current = null;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [model, getParentRowWeights]);

  // Trade History data
  const { trades, isLoading: tradesLoading } = useTradeHistory();
  
  // Funding History data
  const { fundingHistory, isLoading: fundingLoading } = useFundingHistoryData();

  // Products data for Open Orders
  const { products, isLoading: isProductsLoading } = useProductOptions();
  const { setProduct } = useProduct();

  // Open Orders data (reusing open orders panel logic)
  const { openOrders, isLoading: isOpenOrdersLoading, cancelOrder } = useOpenOrders();

  // State to track which order is being cancelled
  const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null);

  // Sorting handlers
  const handleOpenOrdersSorting = useCallback(
    (sorting: SortingState) => {
      setState((prev) => ({ ...prev, openOrdersSorting: sorting }));
    },
    [setState]
  );

  const handleTradeHistorySorting = useCallback(
    (sorting: SortingState) => {
      setState((prev) => ({ ...prev, tradeHistorySorting: sorting }));
    },
    [setState]
  );

  const handleFundingHistorySorting = useCallback(
    (sorting: SortingState) => {
      setState((prev) => ({ ...prev, fundingHistorySorting: sorting }));
    },
    [setState]
  );

  const onOpenOrderRowClick = useCallback(
    (row: OpenOrder) => {
      const product = products?.find((p) => p.id == row.productId);
      if (!product) {
        return;
      }

      setProduct(product);
    },
    [setProduct, products]
  );

  // Handle edit order click
  const handleEditOrderClick = useCallback((order: OpenOrder) => {
    // Get current market price for validation
    const currentPrice = marketPrices[order.productId] || order.currentPrice;
    
    setEditOrderModalData({ order, currentPrice });
  }, [marketPrices]);

  // Handle confirm edit order
  const handleConfirmEditOrder = useCallback(
    async (orderId: string, newQuantity: number, newPrice: number, takeProfit: number | null, stopLoss: number | null) => {
      if (!cancelOrder) return;
      
      const orderData = editOrderModalData?.order;
      if (!orderData) return;
      
      setEditingOrderId(orderId); // Disable the row
      
      try {
        // Step 1: Cancel existing order
        await cancelOrder(orderId);
        
        // Step 2: Place new order with updated values (including TP/SL)
        const request = toBackendOrderRequest(
          orderData.productId,
          orderData.side === "buy" || orderData.side === "long" ? "long" : "short",
          newQuantity,
          newPrice,
          "limit",
          { 
            leverage: getLeverage(orderData.productId),
            timeInForce: "GTC",
            takeProfit: takeProfit,
            stopLoss: stopLoss,
          }
        );
        
        const response = await OrdersApi.placeOrder(request);
        
        // Update store immediately
        useAccountStore.getState().applyMutationResult({ 
          order: response.order,
        });
        
        toast.success(`Order modified: ${orderData.symbol}`);
        setEditOrderModalData(null); // Close modal
        
      } catch (error) {
        // Cancel succeeded but create failed - do NOT recreate original
        const errorMsg = error instanceof Error 
          ? error.message 
          : "Failed to create new order";
        
        toast.error("Order modification failed", {
          description: "Order was cancelled, but the new order could not be created. Please review your balance or try again.",
        });
        
        console.error("Edit order error:", errorMsg);
      } finally {
        setEditingOrderId(null);
      }
    },
    [editOrderModalData, cancelOrder, getLeverage, marketPrices]
  );

  // Handle order cancellation
  const handleCancelOrder = useCallback(
    async (orderId: string) => {
      if (!cancelOrder) return;
      
      try {
        setCancellingOrderId(orderId);
        await cancelOrder(orderId);
        toast.success("Order cancelled successfully");
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to cancel order";
        toast.error(errorMessage);
      } finally {
        setCancellingOrderId(null);
      }
    },
    [cancelOrder]
  );

  // Handle remove take profit from open order
  const handleRemoveOrderTakeProfit = useCallback(
    async (order: OpenOrder) => {
      if (!cancelOrder) return;
      
      setEditingOrderId(order.id);
      
      try {
        // Cancel existing order
        await cancelOrder(order.id);
        
        // Place new order without take profit (keep stop loss if exists)
        const request = toBackendOrderRequest(
          order.productId,
          order.side === "buy" || order.side === "long" ? "long" : "short",
          order.quantity,
          order.limitPrice || 0,
          "limit",
          { 
            leverage: getLeverage(order.productId),
            timeInForce: "GTC",
            takeProfit: null,
            stopLoss: order.stopLoss || null,
          }
        );
        
        const response = await OrdersApi.placeOrder(request);
        useAccountStore.getState().applyMutationResult({ order: response.order });
        toast.success("Take Profit removed");
      } catch (error) {
        toast.error("Failed to remove Take Profit");
      } finally {
        setEditingOrderId(null);
      }
    },
    [cancelOrder, getLeverage]
  );

  // Handle remove stop loss from open order
  const handleRemoveOrderStopLoss = useCallback(
    async (order: OpenOrder) => {
      if (!cancelOrder) return;
      
      setEditingOrderId(order.id);
      
      try {
        // Cancel existing order
        await cancelOrder(order.id);
        
        // Place new order without stop loss (keep take profit if exists)
        const request = toBackendOrderRequest(
          order.productId,
          order.side === "buy" || order.side === "long" ? "long" : "short",
          order.quantity,
          order.limitPrice || 0,
          "limit",
          { 
            leverage: getLeverage(order.productId),
            timeInForce: "GTC",
            takeProfit: order.takeProfit || null,
            stopLoss: null,
          }
        );
        
        const response = await OrdersApi.placeOrder(request);
        useAccountStore.getState().applyMutationResult({ order: response.order });
        toast.success("Stop Loss removed");
      } catch (error) {
        toast.error("Failed to remove Stop Loss");
      } finally {
        setEditingOrderId(null);
      }
    },
    [cancelOrder, getLeverage]
  );

  const tabs = [
    { id: "positions" as TabType, label: "Positions" },
    { id: "open-orders" as TabType, label: "Open Orders" },
    { id: "trade-history" as TabType, label: "Trade History" },
    { id: "funding-history" as TabType, label: "Funding History" },
  ];

  const renderTabContent = () => {
    switch (state.activeTab) {
      case "positions":
        // Show login prompt if not authenticated
        if (!isAuthenticated) {
          return (
            <div className="flex flex-col items-center justify-center h-full p-4 text-center">
              <p className="text-muted-foreground text-sm">
                Log in to view positions
              </p>
              <button
                onClick={() => setLoginModalOpen(true)}
                className="text-primary text-sm mt-2 hover:underline cursor-pointer"
              >
                Click here
              </button>
            </div>
          );
        }
        if (!positionsLoading && sortedPositions.length === 0) {
          return (
            <div className="flex items-center justify-center h-full p-4 text-center">
              <p className="text-muted-foreground text-sm">No open positions yet</p>
            </div>
          );
        }
        // Positions component - cells subscribe directly to Zustand price store
        // No Context wrapper needed - this panel doesn't re-render on price changes
        return (
          <Positions
            data={sortedPositions}
            isLoading={positionsLoading}
            marginMode={marginMode}
            onClosePosition={handleClosePosition}
            onEditTpSl={handleEditTpSl}
            onClearTpSl={handleClearTpSl}
            onEditLeverage={handleEditLeverage}
            onEditMargin={handleEditMargin}
            sorting={state.positionsSorting}
            onSort={handlePositionsSorting}
          />
        );
      case "open-orders":
        // Show login prompt if not authenticated
        if (!isAuthenticated) {
          return (
            <div className="flex flex-col items-center justify-center h-full p-4 text-center">
              <p className="text-muted-foreground text-sm">
                Log in to view open orders
              </p>
              <button
                onClick={() => setLoginModalOpen(true)}
                className="text-primary text-sm mt-2 hover:underline cursor-pointer"
              >
                Click here
              </button>
            </div>
          );
        }
        if (!isOpenOrdersLoading && openOrders.length === 0) {
          return (
            <div className="flex items-center justify-center h-full p-4 text-center">
              <p className="text-muted-foreground text-sm">No open orders yet</p>
            </div>
          );
        }
        return (
          <OpenOrders
            data={openOrders}
            isLoading={isProductsLoading || isOpenOrdersLoading}
            onRowClick={onOpenOrderRowClick}
            onEditOrder={handleEditOrderClick}
            editingOrderId={editingOrderId}
            sorting={state.openOrdersSorting}
            onSort={handleOpenOrdersSorting}
            onCancelOrder={handleCancelOrder}
            isCancelling={cancellingOrderId}
            onRemoveTakeProfit={handleRemoveOrderTakeProfit}
            onRemoveStopLoss={handleRemoveOrderStopLoss}
          />
        );
      case "trade-history":
        if (!tradesLoading && trades.length === 0) {
          return (
            <div className="flex items-center justify-center h-full p-4 text-center">
              <p className="text-muted-foreground text-sm">No trades yet</p>
            </div>
          );
        }
        return (
          <TradeHistory
            data={trades}
            isLoading={tradesLoading}
            sorting={state.tradeHistorySorting}
            onSort={handleTradeHistorySorting}
          />
        );
      case "funding-history":
        if (!fundingLoading && fundingHistory.length === 0) {
          return (
            <div className="flex items-center justify-center h-full p-4 text-center">
              <p className="text-muted-foreground text-sm">No funding distributions yet</p>
            </div>
          );
        }
        return (
          <FundingHistory
            data={fundingHistory}
            isLoading={fundingLoading}
            sorting={state.fundingHistorySorting}
            onSort={handleFundingHistorySorting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <TradingPanelCard noPadding data-tour="positions">
        <div className="flex flex-col h-full">
          {/* Tab Navigation */}
          <div className="flex items-center border-b border-border/40 rounded-t-lg">
            <div className="flex flex-1 items-center overflow-x-auto scrollbar-none">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setState((prev) => ({ ...prev, activeTab: tab.id }))}
                  className={cn(
                    "relative shrink-0 px-4 py-3 text-xs font-normal transition-colors whitespace-nowrap cursor-pointer",
                    state.activeTab === tab.id
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {tab.label}
                  {state.activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                  )}
                </button>
              ))}
            </div>
            <div className="pr-2 flex items-center">
              <div
                className="cursor-row-resize text-muted-foreground hover:text-foreground hover:bg-primary/20 transition-colors flex items-center justify-center p-1 rounded select-none"
                title="Drag to resize panel height"
                onMouseDown={handleResizeMouseDown}
              >
                <ChevronsUpDown className="size-3.5" />
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">
            {renderTabContent()}
          </div>

        </div>
      </TradingPanelCard>

      {/* Edit TP/SL Modal */}
      <EditTpSlModal
        open={isTpSlModalOpen}
        onOpenChange={handleTpSlModalClose}
        position={editTpSlPosition}
        onSuccess={handleTpSlSuccess}
      />

      {/* Edit Order Modal */}
      {editOrderModalData && (
        <EditOrderModal
          open={!!editOrderModalData}
          onOpenChange={(open) => {
            if (!open) setEditOrderModalData(null);
          }}
          order={editOrderModalData.order}
          currentMarketPrice={editOrderModalData.currentPrice}
          onConfirm={handleConfirmEditOrder}
        />
      )}

      {/* Edit Leverage Modal */}
      <EditLeverageModal
        open={isLeverageModalOpen}
        onOpenChange={handleLeverageModalClose}
        position={editLeveragePosition}
        onSuccess={handleLeverageSuccess}
      />

      {/* Edit Position Margin Modal */}
      <EditPositionMarginModal
        open={isMarginModalOpen}
        onOpenChange={handleMarginModalClose}
        position={editMarginPosition}
        onSuccess={handleMarginSuccess}
      />

      {/* Close Position Modal */}
      <ClosePositionModal
        open={isCloseModalOpen}
        onOpenChange={handleCloseModalClose}
        position={closeModalPosition}
        onConfirm={handleConfirmClose}
      />

      {/* Share Result Modal */}
      <ShareResultModal
        open={isShareModalOpen}
        onOpenChange={handleShareModalClose}
        position={shareModalData?.position ?? null}
        pnl={shareModalData?.pnl ?? 0}
        exitPrice={shareModalData?.exitPrice ?? 0}
      />

      {/* Login Modal (for login prompts in tabs) */}
      <LoginModal open={loginModalOpen} onOpenChange={setLoginModalOpen} />
    </>
  );
};

// Open Orders hook - uses centralized hook with WebSocket support
// Now uses store-based orders for instant UI updates (like Positions tab)
const useOpenOrders = () => {
  const { userId } = useUnifiedAuth();
  const { products } = useProductOptions();
  
  // Determine effective user ID (works for both wallet and email users)
  const effectiveUserId = userId;

  // Use centralized hook with WebSocket support - includes cancelOrder function
  // Using openOrders (store-based) for real-time updates instead of orders (HTTP-based)
  const { openOrders: storeOrders, isLoading: isBackendLoading, cancelOrder } = useBackendOrders(
    effectiveUserId ?? undefined
  );

  // Get real-time market prices from price store
  const marketPrices = usePriceStore((state) => state.marketPrices);

  const productsById = useMemo(() => {
    const result: Record<string, Product> = {};
    if (products) {
      for (const product of products) {
        result[product.id] = product;
      }
    }
    return result;
  }, [products]);

  // Check if products are available (needed to display orders)
  const productsReady = products && products.length > 0;

  // Convert store orders to OpenOrder format for display
  const rows = useMemo<OpenOrder[]>(() => {
    const data: OpenOrder[] = [];
    for (const order of storeOrders) {
      // Map market to product (e.g., "BTC-USD" -> product)
      const product = productsById[order.market];
      if (!product) continue;

      // Get current market price from price store
      const currentPrice = marketPrices[order.market] ?? 0;
      const progressPercent = order.size > 0 ? order.filledSize / order.size : 0;

      const openOrder: OpenOrder = {
        id: order.id,
        productId: order.market,
        symbol: product.symbol,
        quote: product.quote,
        base: product.base,
        side: order.side,
        type: order.orderType,
        currentPrice,
        limitPrice: order.orderType === "limit" ? order.price : undefined,
        quantity: order.size,
        progressPercent,
        submittedAt: undefined, // Store orders don't track this yet
        takeProfit: order.takeProfit ?? undefined,
        stopLoss: order.stopLoss ?? undefined,
        notionalValue: undefined, // Store orders don't track this yet
      };

      data.push(openOrder);
    }

    return data;
  }, [storeOrders, marketPrices, productsById]);

  return useMemo(() => {
    // Use backend loading state
    // Also consider loading if products aren't ready yet
    const isLoading = isBackendLoading || (storeOrders.length > 0 && !productsReady);
    return { 
      openOrders: rows, 
      isLoading,
      cancelOrder, // Pass through cancel function
    };
  }, [rows, isBackendLoading, storeOrders.length, productsReady, cancelOrder]);
};

